import React, { useEffect, useMemo, useState } from 'react'
import { useRequest } from 'ahooks'
import styled from 'styled-components'
import { fetchSources, fetchVehicles, HttpError, startLiveStream } from '../../api/vehicles-api'
import { EyerideSource, Vehicle } from '../../types'
import { useJanusStream } from '../../hooks/useJanusStream'

const CAMERA_TABS = ['Front', 'Driver', 'CH3', 'CH4']

const getVehicleIdFromHash = (): string => {
    const hash = window.location.hash || ''
    const match = hash.match(/#\/camera-view\/([^/?#]+)/)
    return match?.[1] || ''
}

const CameraView = (): JSX.Element => {
    const [activeTab, setActiveTab] = useState<string | null>(null)
    const [streamReady, setStreamReady] = useState(false)
    const [streamStartError, setStreamStartError] = useState<string | null>(null)
    const vehicleId = getVehicleIdFromHash()
    const historyVehicle = (window.history.state?.vehicle as Vehicle | undefined) || null
    const numericVehicleId = Number(vehicleId)

    const { data: vehicle, loading } = useRequest(async () => {
        if (!vehicleId) return historyVehicle
        if (historyVehicle && historyVehicle.vehicleId === vehicleId) return historyVehicle

        const vehicles = await fetchVehicles()
        return vehicles.find((item) => item.vehicleId === vehicleId) || null
    })

    const { data: sourcesResponse, loading: loadingSources } = useRequest(
        async () => fetchSources([String(numericVehicleId)]),
        {
            
            ready: Boolean(vehicleId) && !Number.isNaN(numericVehicleId),
        }
    )

    const sourcesForVehicle = useMemo<EyerideSource[]>(() => {
        if (!sourcesResponse || !vehicleId) return []
        return sourcesResponse[vehicleId] || []
    }, [sourcesResponse, vehicleId])

    const tabNames = useMemo<string[]>(() => {
        if (!sourcesForVehicle.length) return CAMERA_TABS
        return sourcesForVehicle.map((source) => source.name)
    }, [sourcesForVehicle])

    const sourceIdByTabName = useMemo<Record<string, string>>(() => {
        return sourcesForVehicle.reduce((acc, source) => {
            acc[source.name] = source.id
            return acc
        }, {} as Record<string, string>)
    }, [sourcesForVehicle])

    useEffect(() => {
        if (!tabNames.length) return
        if (activeTab && !tabNames.includes(activeTab)) {
            setActiveTab(null)
            setStreamReady(false)
        }
    }, [tabNames, activeTab])

    const activeSourceId = activeTab ? sourceIdByTabName[activeTab] : undefined
    const hasSelectedCamera = Boolean(activeTab)
    const {
        videoRef,
        status: janusStatus,
        error: janusError,
    } = useJanusStream(activeSourceId || null, streamReady && Boolean(activeSourceId))

    const handleTabClick = async (tabName: string): Promise<void> => {
        const sourceId = sourceIdByTabName[tabName]
        setActiveTab(tabName)
        setStreamReady(false)
        setStreamStartError(null)

        if (!sourceId) {
            console.warn(`No source id found for tab: ${tabName}`)
            return
        }

        try {
            await startLiveStream(sourceId)
            setStreamReady(true)
        } catch (error) {
            console.error('Failed to start live stream', error)
            if (error instanceof HttpError && error.status === 500) {
                setStreamStartError('Camera not available in the moment')
                return
            }

            setStreamStartError(String(error))
        }
    }

    return (
        <Container>
            <Header>
                <Title>Camera Player</Title>
            </Header>

            <TabsContainer>
                {tabNames.map((tab) => (
                    <TabButton
                        key={tab}
                        type="button"
                        $active={tab === activeTab}
                        data-source-id={sourceIdByTabName[tab] || ''}
                        onClick={() => {
                            void handleTabClick(tab)
                        }}
                    >
                        {tab}
                    </TabButton>
                ))}
            </TabsContainer>

            <PlayerSurface>
                {hasSelectedCamera ? <PlayerLabel>{activeTab}</PlayerLabel> : null}
                {hasSelectedCamera ? <VideoPlayer ref={videoRef} autoPlay playsInline muted /> : null}
                <PlayerHint>
                    {loadingSources
                        ? 'Loading sources...'
                        : !activeTab
                            ? 'No camera selected'
                            : !activeSourceId
                            ? 'No source available for this camera tab'
                            : !streamReady
                                ? 'Click a tab to start the live stream'
                                : janusStatus === 'connecting'
                                    ? 'Connecting to JANUS stream...'
                                    : janusStatus === 'playing'
                                        ? `Playing source ${activeSourceId}`
                                        : streamStartError || janusError || 'Waiting for stream'}
                </PlayerHint>
            </PlayerSurface>
        </Container>
    )
}

const Container = styled.div`
    position:relative;
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    top:35px;
    min-height: calc(100vh - 167px);
    padding: 24px;
    box-sizing: border-box;
`

const Header = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`

const Title = styled.h1`
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    color: ${({ theme }) => theme['gray-800']};
`

const Subtitle = styled.p`
    margin: 0;
    font-size: 14px;
    color: ${({ theme }) => theme['gray-500']};
`

const TabsContainer = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
`

const VehicleInfo = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px 16px;
    padding: 12px;
    border-radius: 12px;
    border: 1px solid ${({ theme }) => theme['gray-200']};
    background-color: ${({ theme }) => theme['gray-0']};

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`

const InfoRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`

const InfoLabel = styled.span`
    font-size: 12px;
    color: ${({ theme }) => theme['gray-500']};
`

const InfoValue = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${({ theme }) => theme['gray-800']};
`

const TabButton = styled.button<{ $active: boolean }>`
    border: 1px solid ${({ theme, $active }) => ($active ? theme['blue-500'] : theme['gray-200'])};
    background: ${({ theme, $active }) => ($active ? theme['blue-200'] : theme['gray-0'])};
    color: ${({ theme, $active }) => ($active ? theme['blue-700'] : theme['gray-600'])};
    border-radius: 999px;
    padding: 10px 16px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
`

const PlayerSurface = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 420px;
    border: 1px dashed ${({ theme }) => theme['gray-300']};
    border-radius: 16px;
    background: linear-gradient(135deg, ${({ theme }) => theme['gray-0']} 0%, ${({ theme }) => theme['gray-100']} 100%);
`

const VideoPlayer = styled.video`
    width: 100%;
    max-width: 960px;
    height: auto;
    min-height: 320px;
    border-radius: 12px;
    background: #000;
`

const PlayerLabel = styled.span`
    font-size: 18px;
    font-weight: 600;
    color: ${({ theme }) => theme['gray-700']};
`

const PlayerHint = styled.span`
    font-size: 14px;
    color: ${({ theme }) => theme['gray-500']};
    text-align: center;
`

export default CameraView